import { IdentityDocumentTypeEnum } from '.spec/self-care/tsTypes';

import { defineMessages } from '@skippay/core/i18n/intl';
import type { MessageDescriptor } from '@skippay/core/i18n/intl';
import { Maybe } from '@skippay/core/utils/types';

type IdentityDocumentMessages = {
	numberLabel: MessageDescriptor;
	typeLabel: MessageDescriptor;
	validityLabel: MessageDescriptor;
};

export type IdentityDocumentTypeLike = Maybe<
	IdentityDocumentTypeEnum | keyof typeof IdentityDocumentTypeEnum
>;

const messages = defineMessages({
	validityLabel: {
		id: 'IdentityDocument.validityLabel',
		defaultMessage: 'Platnost do',
	},
	typeIdCard: {
		id: 'IdentityDocument.type.idCard',
		defaultMessage: 'Občanský průkaz',
	},
	numberIdCard: {
		id: 'IdentityDocument.numberLabel.idCard',
		defaultMessage: 'Číslo občanského průkazu',
	},
	typePassport: {
		id: 'IdentityDocument.type.passport',
		defaultMessage: 'Pas',
	},
	numberPassport: {
		id: 'IdentityDocument.numberLabel.passport',
		defaultMessage: 'Číslo pasu',
	},
	typeDrivingLicense: {
		id: 'IdentityDocument.type.drivingLicense',
		defaultMessage: 'Řidičský průkaz',
	},
	numberDrivingLicense: {
		id: 'IdentityDocument.numberLabel.drivingLicense',
		defaultMessage: 'Číslo řidičského průkazu',
	},
	typeResidencePermit: {
		id: 'IdentityDocument.type.residencePermit',
		defaultMessage: 'Povolení k trvalému pobytu',
	},
	numberResidencePermit: {
		id: 'IdentityDocument.numberLabel.residencePermit',
		defaultMessage: 'Číslo povolení k trvalému pobytu',
	},
	typeUnknown: {
		id: 'IdentityDocument.type.unknown',
		defaultMessage: 'Doklad totožnosti',
	},
	numberUnknown: {
		id: 'IdentityDocument.numberLabel.unknown',
		defaultMessage: 'Číslo a typ dokladu',
	},
});

const idCardMessages: IdentityDocumentMessages = {
	typeLabel: messages.typeIdCard,
	numberLabel: messages.numberIdCard,
	validityLabel: messages.validityLabel,
};

const passportMessages: IdentityDocumentMessages = {
	typeLabel: messages.typePassport,
	numberLabel: messages.numberPassport,
	validityLabel: messages.validityLabel,
};

const drivingLicenseMessages: IdentityDocumentMessages = {
	typeLabel: messages.typeDrivingLicense,
	numberLabel: messages.numberDrivingLicense,
	validityLabel: messages.validityLabel,
};

const residencePermitMessages: IdentityDocumentMessages = {
	typeLabel: messages.typeResidencePermit,
	numberLabel: messages.numberResidencePermit,
	validityLabel: messages.validityLabel,
};

const unknownDocumentMessages: IdentityDocumentMessages = {
	typeLabel: messages.typeUnknown,
	numberLabel: messages.numberUnknown,
	validityLabel: messages.validityLabel,
};

const documentMessagesByType: Record<
	keyof typeof IdentityDocumentTypeEnum,
	IdentityDocumentMessages
> = {
	[IdentityDocumentTypeEnum.ID_CARD]: idCardMessages,
	[IdentityDocumentTypeEnum.PASSPORT]: passportMessages,
	[IdentityDocumentTypeEnum.RESIDENCE_PERMIT]: residencePermitMessages,
	[IdentityDocumentTypeEnum.DRIVING_LICENSE]: drivingLicenseMessages,
};

function getIdentityDocumentMessages(type: IdentityDocumentTypeLike): IdentityDocumentMessages {
	if (!type) {
		return unknownDocumentMessages;
	}

	return (
		documentMessagesByType[type as keyof typeof IdentityDocumentTypeEnum] || unknownDocumentMessages
	);
}

export function getIdentityDocumentTypeLabelMessage(
	type: IdentityDocumentTypeLike
): MessageDescriptor {
	return getIdentityDocumentMessages(type).typeLabel;
}

export function getIdentityDocumentNumberLabelMessage(
	type: IdentityDocumentTypeLike
): MessageDescriptor {
	return getIdentityDocumentMessages(type).numberLabel;
}

export function getIdentityDocumentValidityLabelMessage(
	type: IdentityDocumentTypeLike
): MessageDescriptor {
	return getIdentityDocumentMessages(type).validityLabel;
}
